/**
 * The Employment Law Advice Bureau (EAB) partner banners.
 *
 * Each banner is a distinct image file with its own descriptive file name,
 * alt text and image title, pointing at the matching EAB landing page.
 * Outbound partner links: rendered with rel="sponsored noopener noreferrer".
 */

import disciplinary from "@/assets/eab/free-disciplinary-procedure-advice-for-employers.jpg.asset.json";
import employerHelpline from "@/assets/eab/free-employer-helpline-for-uk-businesses.jpg.asset.json";
import contract from "@/assets/eab/free-employment-contract-advice-for-employers.jpg.asset.json";
import employmentLaw from "@/assets/eab/free-employment-law-advice-for-uk-employers.jpg.asset.json";
import tribunal from "@/assets/eab/free-employment-tribunal-advice-for-employers.jpg.asset.json";
import healthSafety from "@/assets/eab/free-health-and-safety-advice-for-uk-employers.jpg.asset.json";
import hrAdviceLine from "@/assets/eab/free-hr-advice-line-for-employers.jpg.asset.json";
import membership from "@/assets/eab/free-online-membership-for-employers-with-free-hr-toolkits.jpg.asset.json";
import redundancy from "@/assets/eab/free-redundancy-advice-for-uk-employers.jpg.asset.json";
import grievance from "@/assets/eab/free-workplace-grievance-support-for-employers.jpg.asset.json";

export interface EabBanner {
  /** Stable id, also used as the GA4 event label. */
  id: string;
  /** CDN image URL. */
  src: string;
  /** Descriptive file name (SEO). */
  fileName: string;
  /** EAB landing page this banner points at. */
  targetUrl: string;
  alt: string;
  title: string;
  /** Short supporting line rendered under the banner. */
  caption: string;
}

const EAB = "https://www.employment-advice-bureau.org";

export const MEMBERSHIP_BANNER: EabBanner = {
  id: "membership",
  src: membership.url,
  fileName: "free-online-membership-for-employers-with-free-hr-toolkits.jpg",
  targetUrl: `${EAB}/register/#register-form`,
  alt: "EAB's Free Online Membership for Employers with Free HR Toolkits",
  title: "EAB's Free Online Membership for Employers with Free HR Toolkits",
  caption:
    "Free membership with The Employment Law Advice Bureau: HR toolkits, templates and expert guides at no cost to UK employers.",
};

export const eabBanners: readonly EabBanner[] = [
  {
    id: "employer-advice-line",
    src: employerHelpline.url,
    fileName: "free-employer-helpline-for-uk-businesses.jpg",
    targetUrl: `${EAB}/our-services/employer-advice-line/`,
    alt: "EAB's Free Employer Helpline for UK Businesses 24 Hours a day",
    title: "EAB's Free Employer Helpline for UK Businesses 24 Hours a day",
    caption: "Free 24-hour employer advice line from The Employment Law Advice Bureau.",
  },
  {
    id: "hr-advice-line",
    src: hrAdviceLine.url,
    fileName: "free-hr-advice-line-for-employers.jpg",
    targetUrl: `${EAB}/our-services/hr-advice-line-for-employers/`,
    alt: "EAB's Free HR Advice Line for Employers available 24 Hours a day",
    title: "EAB's Free HR Advice Line for Employers available 24 Hours a day",
    caption: "Free HR advice line for UK employers from The Employment Law Advice Bureau.",
  },
  {
    id: "employment-law-advice",
    src: employmentLaw.url,
    fileName: "free-employment-law-advice-for-uk-employers.jpg",
    targetUrl: `${EAB}/`,
    alt: "EAB's Free Employment Law Advice for UK Employers 24 Hours a day",
    title: "EAB's Free Employment Law Advice for UK Employers 24 Hours a day",
    caption: "Free employment law advice for UK employers from The Employment Law Advice Bureau.",
  },
  {
    id: "redundancy-advice",
    src: redundancy.url,
    fileName: "free-redundancy-advice-for-uk-employers.jpg",
    targetUrl: `${EAB}/our-services/redundancy-advice-for-employers/`,
    alt: "EAB's Free Redundancy Advice for UK Employers 24 Hours a day",
    title: "EAB's Free Redundancy Advice for UK Employers 24 Hours a day",
    caption: "Free redundancy support for employers from The Employment Law Advice Bureau.",
  },
  {
    id: "tribunal-advice",
    src: tribunal.url,
    fileName: "free-employment-tribunal-advice-for-employers.jpg",
    targetUrl: `${EAB}/our-services/employment-tribunal-advice/`,
    alt: "EAB's Free Employment Tribunal Advice for Employers 24 Hours a day",
    title: "EAB's Free Employment Tribunal Advice for Employers 24 Hours a day",
    caption: "Free employment tribunal advice for employers from The Employment Law Advice Bureau.",
  },
  {
    id: "contract-advice",
    src: contract.url,
    fileName: "free-employment-contract-advice-for-employers.jpg",
    targetUrl: `${EAB}/our-services/employment-contract-advice/`,
    alt: "EAB's Free Employment Contract Advice for Employers 24 Hours a day",
    title: "EAB's Free Employment Contract Advice for Employers 24 Hours a day",
    caption: "Free employment contract advice for employers from The Employment Law Advice Bureau.",
  },
  {
    id: "health-and-safety-advice",
    src: healthSafety.url,
    fileName: "free-health-and-safety-advice-for-uk-employers.jpg",
    targetUrl: `${EAB}/`,
    alt: "EAB's Free Health and Safety Advice for UK Employers 24 Hours a day",
    title: "EAB's Free Health and Safety Advice for UK Employers 24 Hours a day",
    caption: "Free health and safety advice for UK employers from The Employment Law Advice Bureau.",
  },
  {
    id: "disciplinary-advice",
    src: disciplinary.url,
    fileName: "free-disciplinary-procedure-advice-for-employers.jpg",
    targetUrl: `${EAB}/our-services/employee-disciplinary-procedure-advice/`,
    alt: "EAB's Free Disciplinary Procedure Advice for Employers 24 Hours a day",
    title: "EAB's Free Disciplinary Procedure Advice for Employers 24 Hours a day",
    caption: "Free disciplinary procedure advice for employers from The Employment Law Advice Bureau.",
  },
  {
    id: "grievance-support",
    src: grievance.url,
    fileName: "free-workplace-grievance-support-for-employers.jpg",
    targetUrl: `${EAB}/our-services/workplace-grievance-support-for-employers/`,
    alt: "EAB's Free Workplace Grievance Support for Employers 24 Hours a day",
    title: "EAB's Free Workplace Grievance Support for Employers 24 Hours a day",
    caption: "Free workplace grievance support for employers from The Employment Law Advice Bureau.",
  },
  MEMBERSHIP_BANNER,
];

const byId = new Map(eabBanners.map((b) => [b.id, b]));

/** Exact route path -> banner id. */
const PATH_MAP: Record<string, string> = {
  "/employer-helpline": "employer-advice-line",
  "/hr-helpline": "hr-advice-line",
  "/employment-law-helpline": "employment-law-advice",
  "/redundancy-helpline": "redundancy-advice",
  "/employment-tribunal-helpline": "tribunal-advice",
  "/health-and-safety-helpline": "health-and-safety-advice",
  "/employee-assistance-helpline": "hr-advice-line",
  "/services/redundancy-support": "redundancy-advice",
  "/services/employment-tribunal-support": "tribunal-advice",
  "/services/employment-contract-drafting": "contract-advice",
  "/services/health-and-safety": "health-and-safety-advice",
  "/services/employment-law-advice": "employment-law-advice",
  "/services/staff-handbooks": "contract-advice",
  "/services/hr-outsourcing": "hr-advice-line",
  "/services/hr-for-startups": "hr-advice-line",
  "/services/hr-for-smes": "hr-advice-line",
  "/services/hr-health-check": "hr-advice-line",
  "/services/tupe-advice": "redundancy-advice",
  "/services/recruitment-and-selection": "contract-advice",
  "/services/hr-consultancy": "hr-advice-line",
  "/services/health-and-safety-audits": "health-and-safety-advice",
  "/services/workplace-mediation": "grievance-support",
  "/services/legal-expenses-insurance": "tribunal-advice",
  "/services/risk-assessments": "health-and-safety-advice",
};

/** Keyword fallbacks for content pages (glossary, templates, journal). */
const KEYWORD_MAP: ReadonlyArray<[RegExp, string]> = [
  [/disciplinar|gross-misconduct/i, "disciplinary-advice"],
  [/grievance/i, "grievance-support"],
  [/redundanc/i, "redundancy-advice"],
  [/tribunal|unfair-dismissal|constructive-dismissal/i, "tribunal-advice"],
  [/contract|handbook|new-starter|right-to-work|flexible-working/i, "contract-advice"],
  [/health-and-safety|risk-assessment|stress/i, "health-and-safety-advice"],
];

/** Paths that should always show the free membership banner. */
const MEMBERSHIP_PREFIXES = ["/hr-resources", "/hr-templates", "/hr-calculators", "/hr-glossary"];

export function getEabBanner(path: string): EabBanner {
  const clean = path.replace(/\/+$/, "") || "/";

  const exact = PATH_MAP[clean];
  if (exact) return byId.get(exact) ?? MEMBERSHIP_BANNER;

  if (MEMBERSHIP_PREFIXES.some((p) => clean === p)) return MEMBERSHIP_BANNER;

  for (const [pattern, id] of KEYWORD_MAP) {
    if (pattern.test(clean)) return byId.get(id) ?? MEMBERSHIP_BANNER;
  }

  return MEMBERSHIP_BANNER;
}
